import { GameStatus } from '../../core/constants.js';
import gameState from '../../core/game-state.js';
import { getGameBoard } from '../../gameplay/game-controls.js';
import { suspend as suspendLimit } from '../../gameplay/limit-system/limit-suspension.js';
import { ejectAllPiecesWithGravity, animateBoardWaveEntry } from '../../gameplay/pieces.js';
import { STATE_PATHS } from '../../shared/constants/state-paths.js';
import { MinimalErrorTracker } from '../../shared/log.js';

import { updateGuardState, setSwapConstraints } from './tutorial-guards.js';
import {
  hideGesture,
  renderCard,
  scheduleGestureUpdate,
  setAllowedSelectors,
  setHighlightTarget,
  setOverlayDim,
  showGesture,
} from './tutorial-overlay.js';
import { getTutorialPreset } from './tutorial-presets.js';
import {
  getCurrentStepIndex,
  incrementStepIndex,
  isAdvancingStep,
  setAdvancingStep,
  setCurrentStepIndex,
} from './tutorial-session-state.js';
import { STEP_DEFINITIONS, getStepAtIndex } from './tutorial-step-definitions.js';

export const LIMIT_SUSPEND_REASON = 'tutorial';
const STEP_ADVANCE_FALLBACK_DELAY_MS = 800;
const COMPLETION_AUTO_CLOSE_DELAY_MS = 2000;

let completionAutoCloseTimerId = null;

export function resetStepProgress() {
  setCurrentStepIndex(-1);
}

export function setTutorialState(
  status,
  step,
  allowedActions = [],
  action = 'tutorial_state_update',
) {
  gameState.setState(STATE_PATHS.UI_TUTORIAL, { status, step }, action);
  updateGuardState({ status, step, allowedActions });
}

export function renderPrompt({ onSkip, onStart }) {
  setTutorialState('prompt', null, []);
  setOverlayDim(true);
  setHighlightTarget(null);
  hideGesture();
  setAllowedSelectors([]);
  renderCard({
    message: ['チュートリアルを始めますか？', '基本操作を簡単に説明します。'],
    buttons: [
      { label: 'Skip', variant: 'ghost', onClick: onSkip },
      { label: 'Start', variant: 'primary', onClick: onStart },
    ],
  });
}

export function renderCompletionCard(onFinalize) {
  setTutorialState('completed', null, []);
  setHighlightTarget(null);
  hideGesture();
  setAllowedSelectors([]);

  // Clear any existing auto-close timer
  if (completionAutoCloseTimerId !== null) {
    clearTimeout(completionAutoCloseTimerId);
    completionAutoCloseTimerId = null;
  }

  renderCard({
    message: ['チュートリアル完了です。', '楽しんでくださいね！'],
    buttons: [],
  });

  // Auto-close after delay
  if (onFinalize) {
    completionAutoCloseTimerId = setTimeout(() => {
      completionAutoCloseTimerId = null;
      onFinalize();
    }, COMPLETION_AUTO_CLOSE_DELAY_MS);
  }
}

function cellsToSelectors(cells = []) {
  if (!Array.isArray(cells)) return [];
  return cells.map((cell) => `.piece-shadow[data-row="${cell.row}"][data-col="${cell.col}"]`);
}

function cellToSelector(cell) {
  if (!cell) return null;
  return `.piece-shadow[data-row="${cell.row}"][data-col="${cell.col}"]`;
}

function applyStepGesture(step, fallbackSelector) {
  const gestureType = typeof step?.gesture === 'string' ? step.gesture : null;
  if (!gestureType) {
    hideGesture();
    return;
  }

  const targetSelector = fallbackSelector;
  if (!targetSelector) {
    hideGesture();
    return;
  }

  showGesture(targetSelector, gestureType);
}

function wait(ms) {
  if (!Number.isFinite(ms) || ms <= 0) {
    return Promise.resolve();
  }
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function prepareBoardForStep(step) {
  if (!step?.presetId) {
    return;
  }

  const preset = getTutorialPreset(step.presetId);
  if (!preset) {
    MinimalErrorTracker.warn('TUTORIAL', `Preset not found for step: ${step.presetId}`);
    return;
  }

  const board = getGameBoard();

  try {
    gameState.setState(STATE_PATHS.CORE_STATUS, GameStatus.Processing, 'tutorial_prepare_board');
  } catch (error) {
    MinimalErrorTracker.info('TUTORIAL', 'Failed to set status during tutorial setup', error);
  }

  try {
    await ejectAllPiecesWithGravity(board);
  } catch (error) {
    MinimalErrorTracker.warn('TUTORIAL', 'Failed to eject pieces before tutorial preset', error);
  }

  board.clear();

  try {
    preset.pieces.forEach((pieceDef) => {
      const payload = { color: pieceDef.color };
      if (pieceDef.specialType) {
        payload.specialType = pieceDef.specialType;
      }
      board.setPiece(pieceDef.row, pieceDef.col, payload);
    });
  } catch (error) {
    MinimalErrorTracker.warn('TUTORIAL', 'Failed to set tutorial preset pieces', error);
  }

  try {
    await animateBoardWaveEntry(board);
  } catch (error) {
    MinimalErrorTracker.warn('TUTORIAL', 'Failed to animate tutorial entry', error);
  }

  try {
    gameState.setState(STATE_PATHS.CORE_STATUS, GameStatus.Ready, 'tutorial_board_ready');
  } catch (error) {
    MinimalErrorTracker.info('TUTORIAL', 'Failed to restore game status after preset', error);
  }
}

export async function renderStep(step) {
  if (!step) return;

  hideGesture();

  setTutorialState('running', step.id, []);
  setHighlightTarget(null);
  setAllowedSelectors([]);

  if (step.setupMessage) {
    renderCard({ message: step.setupMessage });
  } else if (step.presetId) {
    renderCard({ message: ['盤面を準備しています…'] });
  }

  if (step.presetId) {
    await prepareBoardForStep(step);
  }

  const highlightSelector =
    step.highlightSelector ?? (step.focusCell ? cellToSelector(step.focusCell) : null);
  const allowedSelectors = [
    ...(step.allowedSelectors ?? []),
    ...cellsToSelectors(step.allowedCells),
  ];
  const fallbackGestureSelector =
    highlightSelector || allowedSelectors.find((selector) => typeof selector === 'string') || null;

  setHighlightTarget(highlightSelector);
  setAllowedSelectors(allowedSelectors);
  setSwapConstraints(step.swapConstraints ?? null);
  applyStepGesture(step, fallbackGestureSelector);
  renderCard({ message: step.message });
  scheduleGestureUpdate();
  suspendLimit(LIMIT_SUSPEND_REASON);
  setTutorialState('running', step.id, step.allowedActions ?? []);
}

export async function advanceToNextStep() {
  const nextIndex = incrementStepIndex();
  const step = getStepAtIndex(nextIndex);
  if (!step) {
    return null;
  }
  await renderStep(step);
  return step;
}

export function getCurrentStepDefinition() {
  const index = getCurrentStepIndex();
  const withinRange = index >= 0 && index < STEP_DEFINITIONS.length;
  return withinRange ? STEP_DEFINITIONS[index] : null;
}

export async function requestStepAdvance(step) {
  if (!step || isAdvancingStep()) {
    return { didAdvance: false, completed: false };
  }

  setAdvancingStep(true);
  try {
    setTutorialState('running', step.id, []);
    setAllowedSelectors([]);
    setSwapConstraints(null);
    setHighlightTarget(null);
    hideGesture();

    if (step.transitionMessage && step.transitionMessage.length > 0) {
      renderCard({ message: step.transitionMessage });
    }

    const delayMs = step.advanceDelayMs ?? STEP_ADVANCE_FALLBACK_DELAY_MS;
    await wait(delayMs);
    const nextStep = await advanceToNextStep();
    return { didAdvance: true, completed: nextStep === null };
  } catch (error) {
    MinimalErrorTracker.warn(
      'TUTORIAL',
      `Failed to advance tutorial from step ${step.id}: ${error?.message ?? error}`,
    );
    return { didAdvance: false, completed: false };
  } finally {
    setAdvancingStep(false);
  }
}

function normalizeBoolean(value) {
  return value === true;
}

function matchesCriteria(details, criteria) {
  const checks = [
    () => criteria.mix === undefined || (criteria.mix ? Boolean(details.mix) : !details.mix),
    () => criteria.orientation === undefined || criteria.orientation === details.orientation,
    () => criteria.size === undefined || Number(details.matchSize) === Number(criteria.size),
    () =>
      criteria.specialCreated === undefined ||
      normalizeBoolean(details.specialCreated) === criteria.specialCreated,
    () =>
      criteria.triggeredSpecial === undefined ||
      normalizeBoolean(details.triggeredSpecial) === criteria.triggeredSpecial,
  ];

  return checks.every((check) => {
    try {
      return check();
    } catch (error) {
      MinimalErrorTracker.warn('TUTORIAL', 'Failed to evaluate tutorial match criteria', error);
      return false;
    }
  });
}

export function matchesForStep(stepId, details = {}) {
  const preset = getTutorialPreset(stepId);
  const criteria = preset?.completion;
  if (criteria) {
    return matchesCriteria(details ?? {}, criteria);
  }
  if (stepId === 'mix') {
    return Boolean(details?.mix);
  }
  return false;
}

export async function handleStartAction({ status, step }) {
  if (status !== 'running' || step !== 'start') {
    return { didAdvance: false, completed: false };
  }
  const currentStep = getCurrentStepDefinition();
  if (!currentStep || currentStep.id !== 'start') {
    return { didAdvance: false, completed: false };
  }
  return requestStepAdvance(currentStep);
}

export async function handleBoardMatchAction({ status, step, details }) {
  if (status !== 'running') {
    return { didAdvance: false, completed: false };
  }
  const currentStep = getCurrentStepDefinition();
  if (!currentStep || currentStep.id !== step) {
    return { didAdvance: false, completed: false };
  }
  if (!matchesForStep(step, details)) {
    return { didAdvance: false, completed: false };
  }
  return requestStepAdvance(currentStep);
}
